import api, { succeedOn404, trimPayload } from './api';
import type { Auditable, Board, BoardCreate } from 'bc-artifacts';
import type { AxiosResponse } from 'axios';
import type { NodeHydratedType, RatedNodeTypeEnum } from 'bc-model';
import type { OpMetadata } from '../stores/sync';
import yup from '../utils/yup';

type BoardNodeType = {
  briefId: number;
  nodeId: number;
  boardId: number;
};

type BoardNodeHydratedType = BoardNodeType & Auditable;
type BoardNodeBlendedType = BoardNodeHydratedType &
  NodeHydratedType & { type: RatedNodeTypeEnum | null };

const boardTextValidationSchema = yup.object().shape({
  text: yup.object().shape({
    title: yup.string().required('required').max(80, 'long'),
    json: yup.object().nullable(),
  }),
});

async function createBoard(
  board: BoardCreate,
  opMetadata?: OpMetadata
): Promise<AxiosResponse<Board>> {
  const { briefId } = board;
  return api.post(`/api/brief/${briefId}/boards`, { ...board, opMetadata });
}

async function deleteBoard(
  briefId: number,
  boardId: number,
  opMetadata?: OpMetadata
): Promise<AxiosResponse<Board>> {
  return api.delete(`/api/brief/${briefId}/board/${boardId}`, { data: { opMetadata } });
}

async function retrieveBoard(
  briefId: number,
  boardId: number,
  returning?: {
    nodes?: boolean;
  }
): Promise<AxiosResponse<Board>> {
  const { nodes: returnNodes } = returning ?? {};
  return api.get(`/api/brief/${briefId}/board/${boardId}`, {
    params: {
      returnNodes,
    },
  });
}

async function retrieveBoards(briefId: number): Promise<AxiosResponse<{ [id: string]: Board }>> {
  return api.get(`/api/brief/${briefId}/boards`);
}

async function updateBoard(
  board: Board,
  returning?: {
    nodes?: boolean;
  },
  opMetadata?: OpMetadata
): Promise<AxiosResponse<Board>> {
  const { briefId, id: boardId } = board;
  const { nodes: returnNodes } = returning ?? {};
  return api.put(
    `/api/brief/${briefId}/board/${boardId}`,
    { ...trimPayload(board), opMetadata },
    {
      params: {
        returnNodes,
      },
      ...succeedOn404,
    }
  );
}

export {
  boardTextValidationSchema,
  createBoard,
  deleteBoard,
  retrieveBoard,
  retrieveBoards,
  updateBoard,
};

export type { BoardNodeBlendedType };
